import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Eye, Settings, Trash2, Server, CheckCircle, AlertCircle, Users, Info } from 'lucide-react';
import DataTable from '../components/common/DataTable';
import type { Column } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import StatCard from '../components/common/StatCard';
import MTGEModal from '../components/mtge/MTGEModal';
import type { MtgeFormData } from '../components/mtge/MTGEModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useToast } from '../components/common/ToastContainer';
import mtgeService from '../services/mtge.service';
import type { MtgeDevice } from '../services/mtge.service';
import { useTenantStore } from '../stores/tenantStore';
import { observabilityService } from '../services/observability.service';
import { formatDate } from '../utils/formatDate';
import { useSettingsStore } from '../stores/settingsStore';
import { usePermissions } from '../hooks/usePermissions';

const MTGEs = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [activeTab, setActiveTab] = useState<'active' | 'pending'>('active');
  const [loading, setLoading] = useState(false);
  const [mtges, setMtges] = useState<MtgeDevice[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingMtge, setEditingMtge] = useState<MtgeDevice | undefined>();
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; mtge?: MtgeDevice }>({ show: false });
  const [deleting, setDeleting] = useState(false);

  // Live online status from telemetry
  const [onlineSerials, setOnlineSerials] = useState<Set<string>>(new Set());

  const fetchMtges = async () => {
    try {
      setLoading(true);
      const [response, telemetry] = await Promise.all([
        mtgeService.list({ pageSize: 100 }),
        observabilityService.getEdgesTelemetry().catch(() => ({ status: 'error', data: {} })),
      ]);
      setMtges(response.items);
      if (response.pagination) {
        setTotalCount(response.pagination.total);
      }

      if (telemetry.status === 'success' && telemetry.data) {
        const online = new Set<string>();
        for (const [edgeId, stats] of Object.entries(telemetry.data)) {
          if (stats && (stats as any).cpu !== undefined) {
            online.add(edgeId);
            online.add(edgeId.toLowerCase());
          }
        }
        setOnlineSerials(online);
      }
    } catch {
      // Silently handle - list will show empty state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMtges();
  }, [selectedTenantId]);

  const isMtgeOnline = (m: MtgeDevice) =>
    onlineSerials.has(m.serial_number) || onlineSerials.has(m.serial_number.toLowerCase());

  const activeMtges = mtges.filter(m => m.status !== 'pending_approval');
  const pendingMtges = mtges.filter(m => m.status === 'pending_approval');
  const onlineCount = activeMtges.filter(isMtgeOnline).length;
  const totalTenantsHosted = mtges.reduce((sum, m) => sum + m.current_tenants, 0);

  const getDisplayStatus = (mtge: MtgeDevice): string => {
    if (['pending_approval', 'approved', 'provisioned'].includes(mtge.status)) {
      const labels: Record<string, string> = {
        pending_approval: 'Pending',
        approved: 'Approved',
        provisioned: 'Provisioned',
      };
      return labels[mtge.status] || mtge.status;
    }
    return isMtgeOnline(mtge) ? 'Online' : 'Offline';
  };

  const columns: Column<MtgeDevice>[] = [
    {
      key: 'device_name',
      label: 'Name',
      sortable: true,
      render: (mtge) => (
        <button
          onClick={() => navigate(`/mtges/${mtge.id}`)}
          className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
        >
          <Server className="w-4 h-4 text-primary-600" />
          <span className="font-medium">{mtge.device_name}</span>
        </button>
      ),
    },
    {
      key: 'serial_number',
      label: 'Serial Number',
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (mtge) => <StatusBadge status={getDisplayStatus(mtge)} />,
    },
    {
      key: 'current_tenants',
      label: 'Tenants',
      sortable: true,
      render: (mtge) => (
        <div className="flex items-center space-x-2">
          <div className="w-16 bg-gray-200 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full ${
                mtge.current_tenants >= mtge.max_tenants ? 'bg-red-500' :
                mtge.current_tenants >= mtge.max_tenants * 0.8 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min((mtge.current_tenants / mtge.max_tenants) * 100, 100)}%` }}
            />
          </div>
          <span className="text-xs font-medium">{mtge.current_tenants}/{mtge.max_tenants}</span>
        </div>
      ),
    },
    {
      key: 'wan_ip',
      label: 'WAN IP',
      render: (mtge) => <span>{mtge.wan_ip || '-'}</span>,
    },
    {
      key: 'agent_version',
      label: 'Version',
      render: (mtge) => <span className="font-mono text-xs">{mtge.agent_version || '-'}</span>,
    },
    {
      key: 'last_seen',
      label: 'Last Seen',
      sortable: true,
      render: (mtge) => {
        if (!mtge.last_seen) return <span>-</span>;
        return <span>{formatDate(mtge.last_seen, useSettingsStore.getState().getDateFormatSettings())}</span>;
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (mtge) => (
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => navigate(`/mtges/${mtge.id}`)}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="View Details"
          >
            <Eye className="w-4 h-4 text-gray-600 hover:text-primary-600" />
          </button>
          {canUpdate && (
            <button
              onClick={() => setEditingMtge(mtge)}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="Edit"
            >
              <Settings className="w-4 h-4 text-gray-600 hover:text-primary-600" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => setDeleteConfirm({ show: true, mtge })}
              className="p-1.5 hover:bg-red-50 rounded transition-colors"
              title="Delete"
            >
              <Trash2 className="w-4 h-4 text-gray-600 hover:text-red-600" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const pendingColumns: Column<MtgeDevice>[] = [
    {
      key: 'device_name',
      label: 'Name',
      render: (mtge) => <span className="font-medium">{mtge.device_name}</span>,
    },
    {
      key: 'serial_number',
      label: 'Serial Number',
    },
    {
      key: 'registered_at',
      label: 'Registered',
      render: (mtge) => {
        if (!mtge.registered_at) return <span>-</span>;
        return <span>{formatDate(mtge.registered_at, useSettingsStore.getState().getDateFormatSettings())}</span>;
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (mtge) => (
        <div className="flex items-center justify-end space-x-2">
          {canUpdate && (
            <button
              onClick={() => handleApprove(mtge)}
              className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
            >
              Approve
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => setDeleteConfirm({ show: true, mtge })}
              className="p-1.5 hover:bg-red-50 rounded transition-colors"
              title="Delete"
            >
              <Trash2 className="w-4 h-4 text-gray-600 hover:text-red-600" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const handleSaveMtge = async (data: MtgeFormData) => {
    try {
      if (editingMtge) {
        await mtgeService.update(editingMtge.id, {
          deviceName: data.device_name,
          maxTenants: data.max_tenants,
          wanIp: data.wan_ip || undefined,
          wanCidr: data.wan_cidr || '/24',
          wanGateway: data.wan_gateway || undefined,
          firmwareVersion: data.firmware_version || undefined,
        });
        toast.success('MTGE Updated', 'Shared gateway has been updated');
        await fetchMtges();
        setEditingMtge(undefined);
        return;
      }

      // Create — return the result so the modal can show credentials
      const result = await mtgeService.create({
        deviceName: data.device_name,
        maxTenants: data.max_tenants,
        baseWgPort: data.base_wg_port,
        wanIp: data.wan_ip || undefined,
        wanCidr: data.wan_cidr || '/24',
        wanGateway: data.wan_gateway || undefined,
      });
      toast.success('MTGE Created', 'Shared gateway has been registered');
      await fetchMtges();
      return result;
    } catch (error: any) {
      toast.error('Operation Failed', error.message || 'Failed to save MTGE');
      throw error;
    }
  };

  const handleApprove = async (mtge: MtgeDevice) => {
    try {
      await mtgeService.approve(mtge.id);
      toast.success('MTGE Approved', `"${mtge.device_name}" has been approved`);
      await fetchMtges();
    } catch (error: any) {
      toast.error('Approval Failed', error.message || 'Failed to approve MTGE');
    }
  };

  const handleDeleteMtge = async () => {
    if (!deleteConfirm.mtge) return;
    try {
      setDeleting(true);
      await mtgeService.delete(deleteConfirm.mtge.id);
      toast.success('MTGE Deleted', `"${deleteConfirm.mtge.device_name}" has been deleted`);
      setDeleteConfirm({ show: false });
      await fetchMtges();
    } catch (error: any) {
      toast.error('Delete Failed', error.message || 'Failed to delete MTGE');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Shared Gateways</h1>
          <p className="text-gray-600 mt-1">Manage multi-tenant gateway edge devices</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={fetchMtges} className="btn-secondary inline-flex items-center space-x-2">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          {canCreate && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Register MTGE</span>
            </button>
          )}
        </div>
      </div>

      {/* Shared Configuration Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-800">Shared Infrastructure</p>
          <p className="text-sm text-blue-700 mt-0.5">
            Shared Gateways (MTGEs) host multiple tenants on a single edge device. This configuration is managed by SuperAdmins only and is not visible to Tenant Admins.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('active')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'active'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Active Gateways
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center space-x-2 ${
                activeTab === 'pending'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>Pending Approval</span>
              {pendingMtges.length > 0 && (
                <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none rounded-full ${
                  activeTab === 'pending' ? 'bg-primary-100 text-primary-700' : 'bg-orange-100 text-orange-700 animate-pulse'
                }`}>
                  {pendingMtges.length}
                </span>
              )}
            </button>
          </nav>
        </div>
      </div>

      {/* Metrics */}
      {activeTab === 'active' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard icon={Server} title="Total Gateways" value={totalCount} color="primary" />
          <StatCard icon={CheckCircle} title="Online" value={onlineCount} color="success" />
          <StatCard icon={AlertCircle} title="Offline" value={Math.max(0, totalCount - onlineCount)} color="secondary" />
          <StatCard icon={Users} title="Tenants Hosted" value={totalTenantsHosted} color="primary" />
        </div>
      )}

      {/* Content */}
      {activeTab === 'active' ? (
        <div className="card">
          <DataTable
            columns={columns}
            data={activeMtges}
            searchPlaceholder="Search gateways by name or serial..."
            loading={loading}
          />
        </div>
      ) : (
        <div className="card">
          <DataTable
            columns={pendingColumns}
            data={pendingMtges}
            searchPlaceholder="Search pending gateways..."
            loading={loading}
          />
        </div>
      )}

      {/* Modals */}
      <MTGEModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveMtge}
        mode="add"
      />
      <MTGEModal
        isOpen={!!editingMtge}
        onClose={() => setEditingMtge(undefined)}
        onSave={handleSaveMtge}
        mtge={editingMtge ? {
          id: editingMtge.id,
          device_name: editingMtge.device_name,
          max_tenants: editingMtge.max_tenants,
          base_wg_port: editingMtge.base_wg_port,
          wan_ip: editingMtge.wan_ip || '',
          wan_cidr: editingMtge.wan_cidr || '/24',
          wan_gateway: editingMtge.wan_gateway || '',
          firmware_version: editingMtge.firmware_version || '',
        } : undefined}
        mode="edit"
      />
      <ConfirmDialog
        isOpen={deleteConfirm.show}
        title="Delete Shared Gateway"
        message={`Are you sure you want to delete "${deleteConfirm.mtge?.device_name}"? All tenant assignments will be removed.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDeleteMtge}
        onClose={() => setDeleteConfirm({ show: false })}
        isLoading={deleting}
      />
    </div>
  );
};

export default MTGEs;
